import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, LogOut, MessageSquare, ShoppingBag, Briefcase, User, Search, MapPin, SlidersHorizontal, Sparkles, Users } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import AccountTools from '../components/AccountTools';

const defaultFilters = {
  keyword: '',
  location: '',
  workplace: 'Any',
  boostedOnly: false,
};

export default function CandidateDashboard({ user }) {
  const [jobs, setJobs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [aiInterviews, setAiInterviews] = useState([]);
  const [slots, setSlots] = useState([]);
  const [showSlots, setShowSlots] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadJobs();
      loadActivities();
      loadBookings();
      loadAiInterviews();
    }
  }, [user]);

  const loadJobs = async (nextFilters = filters) => {
    try {
      setSearching(true);
      const params = new URLSearchParams();
      if (nextFilters.keyword) params.append('keyword', nextFilters.keyword);
      if (nextFilters.location) params.append('location', nextFilters.location);
      if (nextFilters.workplace && nextFilters.workplace !== 'Any') params.append('workplace', nextFilters.workplace);
      if (nextFilters.boostedOnly) params.append('boosted_only', 'true');
      params.append('limit', '50');
      const res = await api.get(`/jobs?${params.toString()}`);
      setJobs(res.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setSearching(false);
    }
  };

  const loadActivities = async (location = filters.location) => {
    try {
      const params = new URLSearchParams();
      if (location) params.append('location', location);
      const res = await api.get(`/company/activities?${params.toString()}`);
      setActivities(res.data);
    } catch (error) {
      console.error('Failed to load company activities');
    }
  };

  const loadBookings = async () => {
    try {
      const res = await api.get(`/bookings/candidate/${user.id}`);
      setBookings(res.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const loadAiInterviews = async () => {
    try {
      const res = await api.get('/ai-interviews/candidate');
      setAiInterviews(res.data);
    } catch (error) {
      console.error('Failed to load Phase 1 interview status');
    }
  };

  const completedAiJobs = useMemo(() => new Set(
    aiInterviews
      .filter((session) => session.submitted_at && ['ready_for_human', 'advanced'].includes(session.status))
      .map((session) => session.job_id)
  ), [aiInterviews]);

  const loadSlots = async (jobId) => {
    try {
      const res = await api.get(`/slots/${jobId}`);
      const availableSlots = res.data.filter(slot => !slot.is_booked);
      setSlots(availableSlots);
    } catch (error) {
      toast.error('Failed to load slots');
    }
  };

  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = async (e) => {
    e.preventDefault();
    await loadJobs(filters);
    await loadActivities(filters.location);
  };

  const clearFilters = async () => {
    setFilters(defaultFilters);
    await loadJobs(defaultFilters);
    await loadActivities('');
  };

  const handleBookSlot = async (slot) => {
    try {
      await api.post('/bookings', {
        job_id: selectedJob.id,
        slot_id: slot.id,
        company_id: selectedJob.company_id,
      });
      toast.success('Interview booked successfully!');
      setShowSlots(false);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book slot');
    }
  };

  const openBooking = async (job) => {
    if (job.ai_interview_mode === 'required' && !completedAiJobs.has(job.id)) {
      toast.error('Complete every Phase 1 question before booking Phase 2');
      navigate(`/ai-interview/${job.id}`);
      return;
    }
    setSelectedJob(job);
    await loadSlots(job.id);
    setShowSlots(true);
  };

  const handleActivityInterest = async (activityId) => {
    try {
      await api.post(`/company/activities/${activityId}/interest`);
      toast.success('Saved. The company can see your interest.');
      loadActivities(filters.location);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save interest');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-4 lg:gap-8">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="The Interview Logo" className="w-10 h-10" />
              <h1 className="hidden text-2xl font-heading font-bold sm:block">The Interview</h1>
            </div>
            <nav className="hidden gap-6 xl:flex">
              <button onClick={() => navigate('/profile')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-profile-btn">
                <User size={16} /> My Profile
              </button>
              <button onClick={() => navigate('/community')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-community-btn">
                <MessageSquare size={16} /> Community
              </button>
              <button onClick={() => navigate('/marketplace')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-marketplace-btn">
                <ShoppingBag size={16} /> Marketplace
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden text-sm text-muted-foreground lg:inline">{user?.full_name}</span>
            <AccountTools onNavigate={navigate} />
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut size={16} className="sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          <nav className="flex w-full gap-5 overflow-x-auto border-t pt-3 text-sm xl:hidden">
            <button onClick={() => navigate('/profile')} className="flex shrink-0 items-center gap-2"><User size={16} /> Profile</button>
            <button onClick={() => navigate('/community')} className="flex shrink-0 items-center gap-2"><MessageSquare size={16} /> Community</button>
            <button onClick={() => navigate('/marketplace')} className="flex shrink-0 items-center gap-2"><ShoppingBag size={16} /> Marketplace</button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-heading font-bold mb-2">Ready for your next role, {user?.full_name}?</h2>
          <p className="text-muted-foreground">Search by role, skill, company, location, or remote preference and book interview slots directly.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border" data-testid="stat-card-bookings">
            <div className="flex items-center gap-4"><div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center"><Calendar className="text-blue-600" /></div><div><div className="text-2xl font-bold">{bookings.length}</div><div className="text-sm text-muted-foreground">Upcoming Interviews</div></div></div>
          </div>
          <div className="bg-white p-6 border" data-testid="stat-card-jobs">
            <div className="flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center"><Briefcase className="text-green-600" /></div><div><div className="text-2xl font-bold">{jobs.length}</div><div className="text-sm text-muted-foreground">Matching Jobs</div></div></div>
          </div>
          <div className="bg-white p-6 border" data-testid="stat-card-activities">
            <div className="flex items-center gap-4"><div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center"><Users className="text-purple-600" /></div><div><div className="text-2xl font-bold">{activities.length}</div><div className="text-sm text-muted-foreground">Company Activities</div></div></div>
          </div>
        </div>

        <form onSubmit={applyFilters} className="mb-8 bg-white border p-5" data-testid="job-search-filters">
          <div className="mb-4 flex items-center gap-2">
            <SlidersHorizontal size={18} />
            <h2 className="text-lg font-heading font-semibold">Find jobs in your area</h2>
          </div>
          <div className="grid lg:grid-cols-[1.2fr_1fr_180px_auto] gap-4 items-end">
            <div>
              <Label htmlFor="job-keyword">Role, skill, or company</Label>
              <Input id="job-keyword" value={filters.keyword} onChange={(e) => updateFilter('keyword', e.target.value)} placeholder="Caregiver, sales, CRM, My Value of Life" data-testid="job-keyword-filter" />
            </div>
            <div>
              <Label htmlFor="job-location-filter">Location</Label>
              <Input id="job-location-filter" value={filters.location} onChange={(e) => updateFilter('location', e.target.value)} placeholder="Dallas, Houston, Remote" data-testid="job-location-filter" />
            </div>
            <div>
              <Label htmlFor="job-workplace-filter">Workplace</Label>
              <select id="job-workplace-filter" value={filters.workplace} onChange={(e) => updateFilter('workplace', e.target.value)} className="w-full border rounded px-3 py-2" data-testid="job-workplace-filter">
                <option>Any</option>
                <option>Remote</option>
                <option>Hybrid</option>
                <option>On-site</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={searching}><Search size={16} className="mr-2" /> Search</Button>
              <Button type="button" variant="outline" onClick={clearFilters}>Clear</Button>
            </div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filters.boostedOnly} onChange={(e) => updateFilter('boostedOnly', e.target.checked)} />
            Show featured and SEO-boosted jobs first only
          </label>
        </form>

        <div className="grid xl:grid-cols-[minmax(0,1.15fr)_minmax(340px,.85fr)] gap-8 items-start">
          <div className="bg-white p-6 border">
            <h2 className="text-xl font-heading font-semibold mb-6">Recommended Jobs</h2>
            <div className="space-y-4" data-testid="jobs-list">
              {jobs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No jobs match that search yet</p>
              ) : (
                jobs.map((job) => (
                  <div key={job.id} className="border p-4 space-y-3 card-hover" data-testid={`job-card-${job.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{job.title}</h3>
                        <p className="text-sm text-muted-foreground">{job.company_name} • {job.location}</p>
                      </div>
                      {job.is_boosted && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Featured</span>}
                    </div>
                    {job.salary_range && <p className="text-sm font-mono">{job.salary_range}</p>}
                    {job.ai_interview_mode && job.ai_interview_mode !== 'off' && (
                      <div className="border bg-blue-50 p-3 text-sm text-blue-950">
                        <div className="flex items-center gap-2 font-semibold">
                          <Sparkles size={15} />
                          Phase 1 AI Interview {job.ai_interview_mode === 'required' ? 'required' : 'available'}
                        </div>
                        <p className="mt-1 text-xs">
                          Complete a structured AI interview first, then move to Phase 2 with a human interviewer.
                        </p>
                      </div>
                    )}
                    <p className="text-sm line-clamp-4">{job.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {(job.requirements || []).slice(0, 5).map((item) => <span key={item} className="rounded border bg-slate-50 px-2 py-1 text-xs">{item}</span>)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {job.ai_interview_mode && job.ai_interview_mode !== 'off' && (
                        <Button size="sm" onClick={() => navigate(`/ai-interview/${job.id}`)} data-testid={`start-ai-interview-${job.id}`}>
                          <Sparkles size={15} className="mr-2" />
                          Start Phase 1 AI
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={job.ai_interview_mode === 'required' ? 'outline' : 'default'}
                        onClick={() => openBooking(job)}
                        data-testid={`book-slot-btn-${job.id}`}
                      >
                        {job.ai_interview_mode === 'required' && !completedAiJobs.has(job.id)
                          ? 'Complete Phase 1 to Unlock'
                          : job.ai_interview_mode === 'required' ? 'Book Phase 2' : 'Book Interview'}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white p-6 border">
              <h2 className="text-xl font-heading font-semibold mb-6">Upcoming Interviews</h2>
              <div className="space-y-4" data-testid="bookings-list">
                {bookings.length === 0 ? <p className="text-muted-foreground text-center py-8">No interviews scheduled yet</p> : bookings.map((booking) => (
                  <div key={booking.id} className="border p-4 space-y-2" data-testid={`booking-card-${booking.id}`}>
                    <h3 className="font-semibold">{booking.job?.title}</h3>
                    <p className="text-sm text-muted-foreground">{booking.job?.company_name}</p>
                    <p className="text-sm">{booking.job?.location}</p>
                    {booking.phase_one_required && !booking.phase_one_complete ? (
                      <Button size="sm" onClick={() => navigate(`/ai-interview/${booking.job_id}`)} data-testid={`complete-phase-one-${booking.id}`}>
                        <Sparkles size={15} className="mr-2" /> Complete Phase 1
                      </Button>
                    ) : (
                      <Button size="sm" onClick={async () => { try { await api.post(`/video/room?booking_id=${booking.id}`); navigate(`/video/${booking.id}`); } catch (error) { toast.error(error.response?.data?.detail || 'Failed to join room'); } }} data-testid={`join-interview-btn-${booking.id}`}>Join Interview</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 border">
              <div className="mb-5 flex items-center gap-2">
                <Sparkles size={18} className="text-purple-600" />
                <h2 className="text-xl font-heading font-semibold">Company Activities</h2>
              </div>
              <div className="space-y-4" data-testid="activities-list">
                {activities.length === 0 ? <p className="text-muted-foreground text-center py-8">No company activities in this area yet</p> : activities.map((activity) => (
                  <div key={activity.id} className="border p-4 space-y-2" data-testid={`activity-card-${activity.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{activity.title}</h3>
                        <p className="text-sm text-muted-foreground">{activity.company_name} • {activity.activity_type}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600"><MapPin size={12} /> {activity.location}</span>
                    </div>
                    <p className="text-sm">{activity.description}</p>
                    <p className="text-xs text-muted-foreground">For: {activity.audience} • Interested: {activity.interested_count || 0}</p>
                    <Button size="sm" variant="outline" onClick={() => handleActivityInterest(activity.id)}>{activity.call_to_action || 'I am interested'}</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showSlots} onOpenChange={setShowSlots}>
        <DialogContent data-testid="slots-dialog">
          <DialogHeader><DialogTitle className="font-heading">Available Time Slots</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {slots.length === 0 ? <p className="text-muted-foreground text-center py-8">No available slots</p> : slots.map((slot) => (
              <div key={slot.id} className="border p-4 flex justify-between items-center" data-testid={`slot-item-${slot.id}`}>
                <div><p className="font-medium">{new Date(slot.start_time).toLocaleString()}</p><p className="text-sm text-muted-foreground">to {new Date(slot.end_time).toLocaleTimeString()}</p></div>
                <Button size="sm" onClick={() => handleBookSlot(slot)} data-testid={`book-slot-action-${slot.id}`}>Book</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
